import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wallet, Activity, Zap, ArrowUpRight, CreditCard, 
  ShoppingBag, ChevronRight, User, Key, Package, 
  Link as LinkIcon, Shirt, CheckCircle, Circle, Copy, Check
} from 'lucide-react';
import { dashboardAPI, partnerAPI, teamAPI } from '../../api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend
} from 'recharts';
import { formatCredits } from '../../utils/credits';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [teamCtx, setTeamCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(false);
  
  // Get user info from localStorage
  const userStr = localStorage.getItem('nanokart_user');
  const user = userStr ? JSON.parse(userStr) : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, onboardingRes, teamCtxRes] = await Promise.all([
        dashboardAPI.getStats(),
        partnerAPI.getOnboarding(),
        teamAPI.getContext().catch(() => null)
      ]);
      setStats(statsRes.data);
      setOnboarding(onboardingRes.data);
      if (teamCtxRes) setTeamCtx(teamCtxRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyPartnerId = () => {
    // Copy the short ID for user-friendly sharing
    const shortId = stats?.partner_short_id || user?.partner_short_id;
    if (shortId) {
      navigator.clipboard.writeText(shortId);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  // Prepare chart data from daily_usage
  const prepareChartData = () => {
    if (!stats?.daily_usage) return [];
    
    const today = new Date();
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const usage = stats.daily_usage[dateStr] || { tryon: 0, moments: 0, credits: 0 };
      days.push({
        date: dayName,
        fullDate: dateStr,
        tryon: usage.tryon || 0,
        moments: usage.moments || 0,
        credits: usage.credits || 0
      });
    }
    return days;
  };

  const chartData = prepareChartData();

  // Step icons mapping
  const stepIcons = {
    complete_profile: User,
    first_tryon: Shirt,
    create_api_key: Key,
    create_inventory: Package,
    create_public_link: LinkIcon
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const partnerName = stats?.partner_name || user?.contact_name || user?.business_name || 'Partner';
  const partnerShortId = stats?.partner_short_id || user?.partner_short_id || '';
  const partnerId = stats?.partner_id || user?.partner_id || '';
  const completedSteps = onboarding?.completed_count || 0;
  const totalSteps = onboarding?.total_steps || 5;
  const isSetupComplete = onboarding?.completed || completedSteps === totalSteps;

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-1" data-testid="welcome-header">
          Welcome, {partnerName}!
        </h1>
        <p className="text-blue-100 text-sm">Here's an overview of your account</p>
        
        {/* Partner ID Display - Shows Short ID */}
        <div className="mt-4 flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2 w-fit">
          <span className="text-blue-100 text-xs">Partner ID:</span>
          <code className="text-white font-mono text-sm font-bold" data-testid="partner-id-display">
            {partnerShortId || partnerId}
          </code>
          <button
            onClick={copyPartnerId}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title="Copy Partner ID"
            data-testid="copy-partner-id-btn"
          >
            {copiedId ? (
              <Check className="w-4 h-4 text-green-300" />
            ) : (
              <Copy className="w-4 h-4 text-blue-200" />
            )}
          </button>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Usage Balance (budget-aware for team members) */}
        <div 
          className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => { if (!teamCtx?.is_team_member) navigate('/partners/wallet'); }}
          data-testid="credits-remaining-card"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <Wallet className="w-5 h-5 text-emerald-600" />
            </div>
            {!teamCtx?.is_team_member && <ArrowUpRight className="w-4 h-4 text-gray-400" />}
          </div>
          {teamCtx?.balance_scope === 'budget' && teamCtx?.budget ? (
            <>
              <p className="text-sm text-gray-500 mb-1">Today&apos;s Budget Left</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="budget-remaining-value">
                ₹{(teamCtx.budget.remaining || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                of ₹{(teamCtx.budget.limit || 0).toLocaleString()}/day · ₹{(teamCtx.budget.used || 0).toLocaleString()} used today
              </p>
              <p className="text-[11px] text-gray-300 mt-0.5">Resets at 12:00 AM IST</p>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-1">Usage Balance</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{(stats?.credits_remaining || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                ₹{(stats?.total_credits_used_7d || 0).toLocaleString()} used this week
              </p>
            </>
          )}
        </div>

        {/* TryOn Calls (7 days) */}
        <div 
          className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate('/partners/tryon-studio')}
          data-testid="tryon-calls-card"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Shirt className="w-5 h-5 text-blue-600" />
            </div>
            <ArrowUpRight className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500 mb-1">TryOn Calls (7d)</p>
          <p className="text-2xl font-bold text-gray-900">
            {stats?.tryon_count_7d || 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {stats?.today_api_calls || 0} today
          </p>
        </div>

        {/* Moments Calls (7 days) */}
        <div 
          className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate('/partners/moments')}
          data-testid="moments-calls-card"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Zap className="w-5 h-5 text-purple-600" />
            </div>
            <ArrowUpRight className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500 mb-1">Moments Calls (7d)</p>
          <p className="text-2xl font-bold text-gray-900">
            {stats?.moments_count_7d || 0}
          </p>
        </div>

        {/* Buy Clicks from Public TryOn */}
        <div 
          className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate('/partners/analytics')}
          data-testid="buy-clicks-card"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-orange-50 rounded-lg">
              <ShoppingBag className="w-5 h-5 text-orange-600" />
            </div>
            <ArrowUpRight className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500 mb-1">Buy Clicks</p>
          <p className="text-2xl font-bold text-gray-900">
            {stats?.public_tryon?.total_buy_clicks || 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {stats?.public_tryon?.buy_click_rate || 0}% conversion rate
          </p>
        </div>
      </div>

      {/* Setup Steps & Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Setup Steps - Compact Design */}
        {!isSetupComplete && !onboarding?.dismissed && (
          <div className="lg:col-span-1 bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="setup-checklist">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Complete Setup</h3>
              <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full font-medium">
                {completedSteps}/{totalSteps}
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full h-2 bg-gray-100 rounded-full mb-4 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500"
                style={{ width: `${onboarding?.progress || 0}%` }}
              />
            </div>

            {/* Steps List */}
            <div className="space-y-2">
              {onboarding?.steps?.map((step) => {
                const IconComponent = stepIcons[step.id] || Circle;
                return (
                  <button
                    key={step.id}
                    onClick={() => navigate(step.action_url)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                      step.completed 
                        ? 'bg-emerald-50 hover:bg-emerald-100' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    data-testid={`setup-step-${step.id}`}
                  >
                    <div className={`p-1.5 rounded-lg ${
                      step.completed ? 'bg-emerald-100' : 'bg-gray-200'
                    }`}>
                      {step.completed ? (
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <IconComponent className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        step.completed ? 'text-emerald-700' : 'text-gray-700'
                      }`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {step.description}
                      </p>
                    </div>
                    <ChevronRight className={`w-4 h-4 flex-shrink-0 ${
                      step.completed ? 'text-emerald-400' : 'text-gray-400'
                    }`} />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* API Usage Chart */}
        <div className={`bg-white rounded-xl border border-gray-100 shadow-sm p-5 ${
          isSetupComplete || onboarding?.dismissed ? 'lg:col-span-2' : 'lg:col-span-2'
        }`} data-testid="api-usage-chart">
          <h3 className="font-semibold text-gray-900 mb-4">API Usage (Last 7 Days)</h3>
          <div className="h-64">
            {chartData.length > 0 && chartData.some(d => d.tryon > 0 || d.moments > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '8px', 
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="tryon" name="TryOn" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="moments" name="Moments" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <Activity className="w-12 h-12 mb-2 opacity-30" />
                <p className="text-sm">No API calls in the last 7 days</p>
                <button 
                  onClick={() => navigate('/partners/tryon-studio')}
                  className="mt-2 text-blue-600 text-sm hover:underline"
                >
                  Make your first call →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Usage Consumption Chart - Only show if setup complete */}
        {(isSetupComplete || onboarding?.dismissed) && (
          <div className="lg:col-span-1 bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="credits-chart">
            <h3 className="font-semibold text-gray-900 mb-4">Usage Consumption</h3>
            <div className="h-64">
              {chartData.length > 0 && chartData.some(d => d.credits > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="credits" 
                      name="Credits"
                      stroke="#10b981" 
                      strokeWidth={2}
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-400">
                  <CreditCard className="w-12 h-12 mb-2 opacity-30" />
                  <p className="text-sm">No usage yet</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Public TryOn Performance - Buy Clicks Graph */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="public-tryon-stats">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Public TryOn Performance</h3>
          <button 
            onClick={() => navigate('/partners/analytics')}
            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
          >
            View Details <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Total Sessions</p>
            <p className="text-xl font-bold text-gray-900">
              {stats?.public_tryon?.total_sessions || 0}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Total Likes</p>
            <p className="text-xl font-bold text-gray-900">
              {stats?.public_tryon?.total_likes || 0}
            </p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Buy Button Clicks</p>
            <p className="text-xl font-bold text-orange-600">
              {stats?.public_tryon?.total_buy_clicks || 0}
            </p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">Conversion Rate</p>
            <p className="text-xl font-bold text-emerald-600">
              {stats?.public_tryon?.buy_click_rate || 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
